import React, { useEffect, useRef, useState } from 'react'
import { Avatar, Badge } from 'antd'
import { connect } from 'react-redux'
import { getAssetLastImage } from '../../appRedux/actions/Asset'

/**
 * AssetThumb
 *
 * Thumbnail for a row in the Assets tab. A register asset carries no inline
 * photos — `photos` only exists on AST and PV records — so this has to come from
 * the asset's own images.
 *
 * Two things keep that affordable on a 400-row table:
 *
 *  1. **Reuse what is already loaded.** A row accounted for by an AST or PV record
 *     has that record's photos inline in the reconcile payload. Those are the
 *     photos of this very asset, taken during this audit, and they cost nothing.
 *     Only rows with no such record (Missing, Out of scope) need a request.
 *
 *  2. **Only fetch what is on screen.** The rest are gated behind an
 *     IntersectionObserver, so scrolling loads a handful at a time instead of
 *     firing 400 requests on first paint. Each asset is fetched once and cached
 *     in redux by id.
 *
 * A row with no photo renders the same muted placeholder as PhotoThumb rather
 * than an empty cell — "no photo" is a real answer, not a gap.
 */

const badgeStyle = {
  backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff', boxShadow: 'none',
  fontSize: 10, height: 15, lineHeight: '15px', minWidth: 15, padding: '0 4px', borderRadius: 8
}

const AssetThumb = ({ assetId, inlinePhotos, onClick, ...props }) => {
  const box = useRef(null)
  const [seen, setSeen] = useState(false)

  const inline = Array.isArray(inlinePhotos) ? inlinePhotos.filter(p => p && p.url) : []
  const hasInline = inline.length > 0

  useEffect(() => {
    if (hasInline || !assetId || seen) return
    const el = box.current
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setSeen(true); io.disconnect() }
    }, { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [assetId, hasInline, seen])

  useEffect(() => {
    if (hasInline || !seen || !assetId) return
    if (!props.assetLastImage[assetId]) props.getAssetLastImage(assetId)
  }, [seen, assetId, hasInline]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetched = (!hasInline && assetId && props.assetLastImage[assetId]) || []
  const url = hasInline ? inline[0].url : (fetched[0] && fetched[0].url)
  const count = hasInline ? inline.length : fetched.length

  const avatar = (
    <Avatar shape="square" size={44} icon="file-image" src={url || undefined}
      style={{ cursor: url ? 'pointer' : 'default', opacity: url ? 1 : 0.45 }}
      onClick={url && onClick ? onClick : undefined} />
  )

  return (
    <span ref={box} title={url ? 'Click to compare and zoom' : 'No photo on this asset'}>
      {count > 1
        ? <Badge count={count} style={badgeStyle} offset={[-4, 36]}>{avatar}</Badge>
        : avatar}
    </span>
  )
}

const mapStateToProps = ({ asset }) => ({ assetLastImage: asset.assetLastImage })

export default connect(mapStateToProps, { getAssetLastImage })(AssetThumb)
